/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
	TextField,
	FormHelperText,
	FormLabel,
	FormControl,
	FormControlLabel,
	Checkbox,
	Typography,
	Grid,
	Tooltip,
} from '@mui/material';
import {
	FIRST_NAME,
	LAST_NAME,
	EMAIL,
	PHONE,
	PHONE2,
	DATE_OF_BIRTH,
	IS_MAIN_CONTACT,
	ROLE,
	EXISTING_CONTACT,
	CONTACT_ERROR,
} from '../validationSchema';
import { useFormikHelper } from '@/hooks/useFormikHelper';
import { NumericFormat } from 'react-number-format';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format } from 'date-fns';
import { safelyParseDate } from '@/utils/date';
import { useFormikContext } from 'formik';

const ContactLabel = ({ title, name, required }) => {
	const labelRef = useRef(null);
	const [isTruncated, setIsTruncated] = useState(false);

	useLayoutEffect(() => {
		if (labelRef.current) {
			const el = labelRef.current;
			setIsTruncated(el.scrollWidth > el.clientWidth);
		}
	}, [title]);

	const labelNode = (
		<FormLabel
			ref={labelRef}
			htmlFor={name}
			required={required}
			sx={{
				whiteSpace: 'nowrap',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				display: 'block',
				maxWidth: '100%',
			}}
		>
			{title}
		</FormLabel>
	);
	return (
		<Tooltip title={isTruncated ? title : ''}>
			<span style={{ width: '100%' }}>{labelNode}</span>
		</Tooltip>
	);
};

export const OldContactFields = ({
	formik,
	loading,
	existingContact,
	index,
	hideHeader = false,
	allowNonPrimary = false,
}) => {
	const { values, setFieldTouched, setFieldValue, getFieldProps, errors, touched } =
		formik ?? useFormikContext();
	const { getError, getErrorMessage } = useFormikHelper(formik);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const [errorMessage, setErrorMessage] = useState('');

	useEffect(() => {
		setErrorMessage('');
		setFieldValue(CONTACT_ERROR, false);
		setFieldTouched(getFieldName(EMAIL), false);
	}, [existingContact]);

	const getFieldName = (field) => (index !== undefined ? `contacts.${index}.${field}` : field);

	const isFieldDisabled = (fieldValue) => loading || existingContact?.[fieldValue] != null;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { getFeatureFlags } = useFeatureFlagContext();
	const contactNonRequired = Boolean(getFeatureFlags('contactNonRequired')?.active);
	const prospects2 = Boolean(getFeatureFlags('prospects2')?.active);

	useEffect(() => {
		if (values.contacts?.length > 0 && !allowNonPrimary) {
			const hasMainContact = values.contacts.some((contact) => contact[IS_MAIN_CONTACT]);
			if (!hasMainContact) {
				setFieldValue(`contacts[0].${IS_MAIN_CONTACT}`, true);
			}
		}
	}, [values.contacts, setFieldValue]);

	const isExistingContact = values.contacts?.[index]?.[EXISTING_CONTACT];

	const isFolioOrCbnLedger =
		userDetails?.broker.ledger === 'folio' || userDetails?.broker.ledger.startsWith('cbn');

	const required =
		!((values?.isProspect && prospects2) || contactNonRequired || values?.contactNonRequired) ||
		isFolioOrCbnLedger;

	const checkboxDisabled =
		loading ||
		(!allowNonPrimary &&
			(values.contacts.length <= 1 ||
				(index === 0 &&
					values.contacts.filter((c, i) => i !== index).every((c) => !c.isMainContact))));

	return (
		<>
			<Grid container alignItems='center' rowSpacing={1} columnSpacing={2}>
				<>
					{(!hideHeader || index !== 0) && (
						<Grid>
							<Typography variant='form_label' fontSize={'16px'}>
								Additional contact details
							</Typography>
						</Grid>
					)}
					<Grid>
						<FormControlLabel
							control={
								<Checkbox
									name={getFieldName(IS_MAIN_CONTACT)}
									disabled={checkboxDisabled}
									checked={values.contacts?.[index]?.[IS_MAIN_CONTACT] || false}
									onChange={(e) => {
										const isChecked = e.target.checked;
										if (isChecked) {
											const updatedContacts = values.contacts.map(
												(contact, i) => ({
													...contact,
													[IS_MAIN_CONTACT]: i === index,
												}),
											);
											setFieldValue('contacts', updatedContacts);
										} else if (allowNonPrimary) {
											setFieldValue(getFieldName(IS_MAIN_CONTACT), false);
										} else {
											const updatedContacts = values.contacts.map(
												(contact, i) => ({
													...contact,
													[IS_MAIN_CONTACT]:
														i === index
															? false
															: contact[IS_MAIN_CONTACT],
												}),
											);
											setFieldValue('contacts', updatedContacts);
										}
									}}
								/>
							}
							label='Primary contact'
						/>
					</Grid>
				</>
			</Grid>
			<Grid container spacing={1} sx={{ width: '100%' }}>
				<Grid size={{ xs: 12, md: 6 }}>
					<FormControl fullWidth required={true}>
						<ContactLabel name='first-name' required={true} title='First Name' />

						<TextField
							id='first-name'
							fullWidth
							name={getFieldName(FIRST_NAME)}
							{...getFieldProps(getFieldName(FIRST_NAME))}
							error={
								getError(getFieldName(FIRST_NAME)) ||
								(errors.contacts?.[index]?.[FIRST_NAME] &&
									touched.contacts?.[index]?.[FIRST_NAME])
							}
							helperText={
								(getError(getFieldName(FIRST_NAME)) ||
									(errors.contacts?.[index]?.[FIRST_NAME] &&
										touched.contacts?.[index]?.[FIRST_NAME])) &&
								getErrorMessage(getFieldName(FIRST_NAME))
							}
							disabled={isFieldDisabled(FIRST_NAME) || isExistingContact}
							required
						/>
					</FormControl>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<FormControl fullWidth required={true}>
						<ContactLabel name='last-name' required={true} title='Last Name' />

						<TextField
							id='last-name'
							fullWidth
							name={getFieldName(LAST_NAME)}
							{...getFieldProps(getFieldName(LAST_NAME))}
							error={
								getError(getFieldName(LAST_NAME)) ||
								(errors.contacts?.[index]?.[LAST_NAME] &&
									touched.contacts?.[index]?.[LAST_NAME])
							}
							helperText={
								(getError(getFieldName(LAST_NAME)) ||
									(errors.contacts?.[index]?.[LAST_NAME] &&
										touched.contacts?.[index]?.[LAST_NAME])) &&
								getErrorMessage(getFieldName(LAST_NAME))
							}
							disabled={isFieldDisabled(LAST_NAME) || isExistingContact}
							required
						/>
					</FormControl>
				</Grid>
				<Grid size={{ xs: 12, md: 4 }}>
					<FormControl fullWidth required>
						<ContactLabel
							name={getFieldName(PHONE)}
							required={required}
							title='Phone Number'
						/>

						<NumericFormat
							customInput={TextField}
							decimalScale={0}
							fixedDecimalScale
							allowNegative={false}
							allowLeadingZeros
							onValueChange={({ value }) => {
								setFieldValue(getFieldName(PHONE), value);
							}}
							{...getFieldProps(getFieldName(PHONE))}
							required={required}
							disabled={isFieldDisabled(PHONE) || isExistingContact}
							id={getFieldName(PHONE)}
							name={getFieldName(PHONE)}
							error={
								required &&
								(getError(getFieldName(PHONE)) ||
									(errors.contacts?.[index]?.phoneNumber &&
										touched.contacts?.[index]?.phoneNumber))
							}
							helperText={
								(required || errors.contacts?.[index]?.phoneNumber) &&
								getErrorMessage(getFieldName(PHONE))
							}
						/>
					</FormControl>
				</Grid>
				<Grid size={{ xs: 12, md: 4 }}>
					<FormControl fullWidth>
						<ContactLabel
							name={getFieldName(PHONE2)}
							title='Alternative Phone Number'
						/>

						<NumericFormat
							customInput={TextField}
							decimalScale={0}
							fixedDecimalScale
							allowNegative={false}
							allowLeadingZeros
							onValueChange={({ value }) => {
								setFieldValue(getFieldName(PHONE2), value);
							}}
							{...getFieldProps(getFieldName(PHONE2))}
							disabled={isFieldDisabled(PHONE2) || isExistingContact}
							id={getFieldName(PHONE2)}
							name={getFieldName(PHONE2)}
							error={
								getError(getFieldName(PHONE2)) ||
								(errors.contacts?.[index]?.phoneNumber2 &&
									touched.contacts?.[index]?.phoneNumber2)
							}
							helperText={
								(getError(getFieldName(PHONE2)) ||
									(errors.contacts?.[index]?.phoneNumber2 &&
										touched.contacts?.[index]?.phoneNumber2)) &&
								getErrorMessage(getFieldName(PHONE2))
							}
						/>
					</FormControl>
				</Grid>
				<Grid size={{ xs: 12, md: 4 }}>
					<FormControl fullWidth>
						<ContactLabel name={getFieldName(DATE_OF_BIRTH)} title='Date of Birth' />

						<LocalizationProvider dateAdapter={AdapterDateFns}>
							<DatePicker
								value={
									values.contacts?.[index]?.[DATE_OF_BIRTH]
										? safelyParseDate(values.contacts[index][DATE_OF_BIRTH])
										: null
								}
								onChange={(newValue) => {
									// Store the value directly as a Date object
									// if passed in a date object, only store date without the time value
									if (newValue?.toISOString) {
										newValue = format(newValue, 'yyyy-MM-dd');
									}
									setFieldValue(getFieldName(DATE_OF_BIRTH), newValue);
								}}
								disabled={isFieldDisabled(DATE_OF_BIRTH) || isExistingContact}
								maxDate={new Date()}
								format='dd/MM/yyyy'
								slotProps={{
									textField: {
										fullWidth: true,
										error:
											getError(getFieldName(DATE_OF_BIRTH)) ||
											(errors.contacts?.[index]?.dateOfBirth &&
												touched.contacts?.[index]?.dateOfBirth),
										helperText:
											(getError(getFieldName(DATE_OF_BIRTH)) ||
												(errors.contacts?.[index]?.dateOfBirth &&
													touched.contacts?.[index]?.dateOfBirth)) &&
											getErrorMessage(getFieldName(DATE_OF_BIRTH)),
									},
								}}
							/>
						</LocalizationProvider>
					</FormControl>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<FormControl fullWidth required={required}>
						<ContactLabel
							name={getFieldName(EMAIL)}
							required={required}
							title='Email Address'
						/>

						<TextField
							fullWidth
							id={getFieldName(EMAIL)}
							name={getFieldName(EMAIL)}
							{...getFieldProps(getFieldName(EMAIL))}
							error={
								!isExistingContact &&
								((touched.contacts?.[index]?.emailAddress &&
									!!errors.contacts?.[index]?.emailAddress) ||
									!!errorMessage)
							}
							helperText={
								!isExistingContact &&
								(errorMessage ||
									(touched.contacts?.[index]?.emailAddress &&
										errors.contacts?.[index]?.emailAddress) ||
									'We will use this email to contact you.')
							}
							disabled={isFieldDisabled(EMAIL) || isExistingContact}
							required={required}
							type='email'
							onBlur={async () => {
								if (isExistingContact) return;

								setFieldValue(CONTACT_ERROR, false);
								setErrorMessage('');
							}}
						/>
					</FormControl>
				</Grid>

				<Grid size={{ xs: 12, md: 6 }}>
					<FormControl fullWidth>
						<ContactLabel name={getFieldName(ROLE)} title='Role' />

						<TextField
							fullWidth
							disabled={loading}
							name={getFieldName(ROLE)}
							{...getFieldProps(getFieldName(ROLE))}
							error={getError(getFieldName(ROLE))}
							helperText={getErrorMessage(getFieldName(ROLE))}
						/>
					</FormControl>
					<FormHelperText>
						{"Contact's role/relationship to the client (e.g director)."}
					</FormHelperText>
				</Grid>
			</Grid>
		</>
	);
};
